import { useState } from "react";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import { FIELD, Fieldset, Req, Select } from "@/components/join/fields";
import CompanyAutocomplete from "@/components/join/CompanyAutocomplete";
import JoinSuccess from "@/components/join/JoinSuccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TERMS_URL = "/PayMe_General_terms_and_conditions_Version_2.pdf";
const SALUTATIONS = ["Dr", "Miss", "Mr", "Mrs", "Ms", "Prof"];
const STATES = ["Tasmania", "Northern Territory", "Victoria", "South Australia", "Queensland", "Western Australia", "Australian Capital Territory", "New South Wales"];
const SOURCES = ["Employer Recruitment Company", "Return Customer", "Social Media – Facebook", "Social Media - Instagram", "Social Media – LinkedIn", "Google Search", "Other Search Engine", "Referral from another contractor", "Bus Advertising", "RiotACT"];
const DOB_RE = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
    salutation: "", first_name: "", last_name: "", preferred_first_name: "", state: "", mobile_number: "", email: "", date_of_birth: "",
    recruitment_company: "", recruiter_first_name: "", recruiter_last_name: "", recruiter_email: "", recruiter_phone_number: "",
    employment_contract_source: "", referred_from: "", agree_terms: false,
};

function validate(f, companyMatched) {
    const e = {};
    for (const k of ["salutation", "first_name", "last_name", "state", "mobile_number", "employment_contract_source"]) if (!String(f[k]).trim()) e[k] = true;
    if (!EMAIL_RE.test(f.email)) e.email = true;
    if (!DOB_RE.test(f.date_of_birth.trim())) e.date_of_birth = true;
    if (companyMatched) {
        if (!f.recruiter_first_name.trim()) e.recruiter_first_name = true;
        if (!f.recruiter_last_name.trim()) e.recruiter_last_name = true;
        if (!EMAIL_RE.test(f.recruiter_email)) e.recruiter_email = true;
    }
    return e;
}

export default function JoinForm() {
    const [form, setForm] = useState(EMPTY);
    const [companyMatched, setCompanyMatched] = useState(false);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const inv = (k) => (errors[k] ? "true" : "false");

    const onMatch = (matched) => {
        setCompanyMatched(matched);
        if (!matched) setForm((f) => ({ ...f, recruiter_first_name: "", recruiter_last_name: "", recruiter_email: "", recruiter_phone_number: "" }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const errs = validate(form, companyMatched);
        setErrors(errs);
        if (Object.keys(errs).length) {
            document.querySelector('[aria-invalid="true"]')?.focus();
            return;
        }
        setSending(true);
        setFailed(false);
        try {
            const clean = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v]));
            if (!companyMatched) Object.assign(clean, { recruiter_first_name: null, recruiter_last_name: null, recruiter_email: null, recruiter_phone_number: null });
            if (form.employment_contract_source !== "Referral from another contractor") clean.referred_from = null;
            await axios.post(`${API}/join`, clean);
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            setFailed(true);
        } finally {
            setSending(false);
        }
    };

    if (done) return <JoinSuccess />;

    return (
        <form onSubmit={submit} noValidate className="space-y-8" data-testid="join-form">
            <Fieldset legend="Personal Details" testId="join-personal-details">
                <div className="grid gap-4 pl-3 md:grid-cols-12">
                    <div className="md:col-span-3"><Req><Select placeholder="Salutation" options={SALUTATIONS} value={form.salutation} onChange={set("salutation")} aria-invalid={inv("salutation")} aria-label="Salutation" testId="join-salutation" /></Req></div>
                    <div className="md:col-span-5"><Req><input className={FIELD} placeholder="First Name" maxLength={50} value={form.first_name} onChange={set("first_name")} aria-invalid={inv("first_name")} aria-label="First Name" data-testid="join-first-name" /></Req></div>
                    <div className="md:col-span-4"><Req><input className={FIELD} placeholder="Last Name" maxLength={50} value={form.last_name} onChange={set("last_name")} aria-invalid={inv("last_name")} aria-label="Last Name" data-testid="join-last-name" /></Req></div>
                </div>
                <div className="pl-3"><input className={FIELD} placeholder="Preferred First Name" maxLength={100} value={form.preferred_first_name} onChange={set("preferred_first_name")} aria-label="Preferred First Name" data-testid="join-preferred-name" /></div>
                <div className="grid gap-4 pl-3 md:grid-cols-3">
                    <Req><Select placeholder="State" options={STATES} value={form.state} onChange={set("state")} aria-invalid={inv("state")} aria-label="State" testId="join-state" /></Req>
                </div>
                <div className="grid gap-4 pl-3 md:grid-cols-2">
                    <Req><input className={FIELD} type="tel" placeholder="Mobile Number" maxLength={20} value={form.mobile_number} onChange={set("mobile_number")} aria-invalid={inv("mobile_number")} aria-label="Mobile Number" data-testid="join-mobile" /></Req>
                </div>
                <div className="grid gap-4 pl-3 md:grid-cols-2">
                    <Req><input className={FIELD} type="email" placeholder="Email" maxLength={100} value={form.email} onChange={set("email")} aria-invalid={inv("email")} aria-label="Email" data-testid="join-email" /></Req>
                    <Req>
                        <input className={FIELD} placeholder="Birthdate" inputMode="numeric" maxLength={10} value={form.date_of_birth} onChange={set("date_of_birth")} aria-invalid={inv("date_of_birth")} aria-label="Birthdate" data-testid="join-dob" />
                        <span className="mt-1 block font-mono text-[11px] text-muted-foreground">dd/mm/yyyy</span>
                    </Req>
                </div>
            </Fieldset>

            <Fieldset legend="Recruitment Details" testId="join-recruitment-details">
                <label className="block text-sm font-medium">
                    Employer Recruitment Company{" "}
                    <span className="text-xs italic text-muted-foreground">(Leave it blank if you are unable to find your Employer Recruitment Company here)</span>
                </label>
                <CompanyAutocomplete value={form.recruitment_company} onChange={(v) => setForm((f) => ({ ...f, recruitment_company: v }))} onMatch={onMatch} className={FIELD} />
                {companyMatched && (
                    <div className="space-y-4 pl-3 pt-2" data-testid="join-recruiter-details">
                        <div className="grid gap-4 md:grid-cols-2">
                            <Req><input className={FIELD} placeholder="Recruiter First Name" maxLength={50} value={form.recruiter_first_name} onChange={set("recruiter_first_name")} aria-invalid={inv("recruiter_first_name")} aria-label="Recruiter First Name" data-testid="join-recruiter-first-name" /></Req>
                            <Req><input className={FIELD} placeholder="Recruiter Last Name" maxLength={50} value={form.recruiter_last_name} onChange={set("recruiter_last_name")} aria-invalid={inv("recruiter_last_name")} aria-label="Recruiter Last Name" data-testid="join-recruiter-last-name" /></Req>
                        </div>
                        <Req><input className={FIELD} type="email" placeholder="Recruiter Email" maxLength={100} value={form.recruiter_email} onChange={set("recruiter_email")} aria-invalid={inv("recruiter_email")} aria-label="Recruiter Email" data-testid="join-recruiter-email" /></Req>
                        <input className={FIELD} type="tel" placeholder="Recruiter Phone Number" maxLength={50} value={form.recruiter_phone_number} onChange={set("recruiter_phone_number")} aria-label="Recruiter Phone Number" data-testid="join-recruiter-phone" />
                    </div>
                )}
            </Fieldset>

            <Fieldset legend="Where did you hear about us?" testId="join-source">
                <label className="block text-sm font-medium">Please let us know where you heard about PayMe</label>
                <div className="grid gap-4 pl-3 md:grid-cols-3">
                    <Req><Select placeholder="" options={SOURCES} value={form.employment_contract_source} onChange={set("employment_contract_source")} aria-invalid={inv("employment_contract_source")} aria-label="Where did you hear about us" testId="join-source-select" /></Req>
                    {form.employment_contract_source === "Referral from another contractor" && (
                        <input className={`${FIELD} md:col-span-2`} placeholder="Referral Contractors Name" maxLength={250} value={form.referred_from} onChange={set("referred_from")} aria-label="Referral Contractors Name" data-testid="join-referral-name" />
                    )}
                </div>
            </Fieldset>

            <Fieldset legend="Acknowledgements" testId="join-acknowledgements">
                <label className="flex items-start gap-4 pl-3">
                    <span className="relative mt-1 shrink-0">
                        <span aria-hidden="true" className="absolute -left-3 -top-1 font-bold text-destructive">*</span>
                        <input type="checkbox" checked={form.agree_terms} onChange={(e) => setForm((f) => ({ ...f, agree_terms: e.target.checked }))} className="h-5 w-5 accent-primary" data-testid="join-agree-checkbox" />
                    </span>
                    <span className="text-sm leading-relaxed">
                        I agree to the{" "}
                        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline-offset-2 hover:underline" data-testid="join-terms-link">PayMe Terms and Conditions</a>{" "}
                        under which I engage PayMe to provide payroll services. A copy of the PayMe Terms and Conditions will be emailed to you when you submit the Join Form.
                        <span className="mt-2 block text-xs text-muted-foreground">A copy of the PayMe Service Agreement will be emailed to you when you submit the Join Form.</span>
                    </span>
                </label>
            </Fieldset>

            {Object.keys(errors).length > 0 && (
                <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="join-validation-error">
                    Please complete the highlighted required fields.
                </p>
            )}
            {failed && (
                <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="join-submit-error">
                    Failed to submit the request. Please try again.
                </p>
            )}

            <div className="flex justify-end">
                <button
                    type="submit"
                    disabled={!form.agree_terms || sending}
                    title={form.agree_terms ? "" : "The form can only be submitted once you have agreed to the PayMe Service Agreement."}
                    data-testid="join-submit-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-primary-foreground transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
                >
                    {sending ? "Sending…" : "Send"} <ChevronRight size={16} />
                </button>
            </div>
        </form>
    );
}
